import { getLeagueTexts } from "../../leagues/shared/leagueTexts";
import type { KeyboardEvent } from "react";
import { siteTexts } from "../../../shared/i18n/siteTexts";
import type { AdminNavItem } from "../types/admin";

type AdminSidebarIconName =
  | AdminNavItem
  | "tournaments"
  | "dashboard"
  | "finance"
  | "notifications"
  | "stock";

function AdminSidebarIcon({ item }: { item: AdminSidebarIconName }) {
  if (item === "history") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M4 20V10" />
        <path d="M10 20V4" />
        <path d="M16 20v-7" />
        <path d="M22 20H2" />
      </svg>
    );
  }
  if (item === "tournaments") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M8 21h8" />
        <path d="M12 17v4" />
        <path d="M7 4h10v5a5 5 0 0 1-10 0V4Z" />
        <path d="M5 5H3v2a4 4 0 0 0 4 4" />
        <path d="M19 5h2v2a4 4 0 0 1-4 4" />
      </svg>
    );
  }
  if (item === "users") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    );
  }
  if (item === "clubs") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M3 21h18" />
        <path d="M5 21V7l7-4 7 4v14" />
        <path d="M9 21v-6h6v6" />
        <path d="M9 10h.01" />
        <path d="M15 10h.01" />
      </svg>
    );
  }
  if (item === "registrations") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M3 7h18" />
        <path d="M7 3v8" />
        <path d="M17 3v8" />
        <path d="M5 21h8a1 1 0 0 0 1-1V7H4v13a1 1 0 0 0 1 1z" />
        <path d="m20 15-3 3-1.5-1.5" />
      </svg>
    );
  }
  if (item === "categoryRequests") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        <path d="M8 9h8" />
        <path d="M8 13h5" />
      </svg>
    );
  }
  if (item === "payments") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <rect x="2" y="5" width="20" height="14" rx="2" />
        <path d="M2 10h20" />
        <path d="M6 15h4" />
      </svg>
    );
  }
  if (item === "dashboard") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M4 13h6V4H4v9Z" />
        <path d="M14 20h6V4h-6v16Z" />
        <path d="M4 20h6v-3H4v3Z" />
      </svg>
    );
  }
  if (item === "finance") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <rect x="3" y="6" width="18" height="12" rx="2" />
        <path d="M7 14h.01" />
        <path d="M11 14h6" />
      </svg>
    );
  }
  if (item === "notifications") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" />
        <path d="M10 21h4" />
      </svg>
    );
  }
  if (item === "stock") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="m21 16-9 5-9-5V8l9-5 9 5v8Z" />
        <path d="m3.5 8.5 8.5 5 8.5-5" />
        <path d="M12 22v-8.5" />
      </svg>
    );
  }
  if (item === "coupons") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M4 7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v3a2 2 0 0 0 0 4v3a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-3a2 2 0 0 0 0-4z" />
        <path d="M9 9h.01" />
        <path d="M15 15h.01" />
        <path d="m15 9-6 6" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M3 12h18" />
      <path d="M12 3v18" />
      <path d="M4 4h16v16H4z" />
    </svg>
  );
}

type Props = {
  adminNavCollapsed: boolean;
  adminNavOpen: boolean;
  historyNavActive: boolean;
  isAdmin: boolean;
  isOrganizer: boolean;
  leaguesNavActive?: boolean;
  onClose: () => void;
  onNavigateCategoryRequests: () => void;
  onNavigateClubs: () => void;
  onNavigateCoupons: () => void;
  onNavigateHistory: () => void;
  onNavigatePayments: () => void;
  onNavigateRegistrations: () => void;
  onNavigateTournaments: () => void;
  onNavigateUsers: () => void;
  onToggleDesktop: () => void;
  tournamentsNavActive: boolean;
  usersNavActive: boolean;
  clubsNavActive: boolean;
  categoryRequestsNavActive: boolean;
  registrationsNavActive: boolean;
  paymentsNavActive: boolean;
  couponsNavActive: boolean;
};

export default function AdminSidebar({
  adminNavCollapsed,
  adminNavOpen,
  categoryRequestsNavActive,
  clubsNavActive,
  couponsNavActive,
  historyNavActive,
  isAdmin,
  isOrganizer,
  leaguesNavActive,
  onClose,
  onNavigateCategoryRequests,
  onNavigateClubs,
  onNavigateCoupons,
  onNavigateHistory,
  onNavigatePayments,
  onNavigateRegistrations,
  onNavigateTournaments,
  onNavigateUsers,
  onToggleDesktop,
  paymentsNavActive,
  registrationsNavActive,
  tournamentsNavActive,
  usersNavActive,
}: Props) {
  const t = siteTexts.admin;
  const onSidebarItemKeyDown = (
    event: KeyboardEvent<HTMLDivElement>,
    action: () => void,
  ) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      action();
    }
  };

  return (
    <>
      <button
        type="button"
        className={`admin-sidebar-backdrop${adminNavOpen ? " open" : ""}`}
        onClick={onClose}
        aria-label={t.sidebar.closePanelMenu}
      />

      <aside
        className={`admin-sidebar${adminNavOpen ? " open" : ""}`}
        aria-label={t.sidebar.adminMenu}
      >
        <button
          type="button"
          className="admin-sidebar-toggle"
          onClick={onToggleDesktop}
          aria-label={
            adminNavCollapsed ? t.sidebar.openSideMenu : t.sidebar.closeSideMenu
          }
          aria-expanded={!adminNavCollapsed}
        >
          {adminNavCollapsed ? ">" : "<"}
        </button>

        <div className="admin-sidebar-content">
          <div className="admin-sidebar-header">
            <p className="admin-sidebar-kicker">{t.sidebar.kicker}</p>
            <h2>{isAdmin ? t.sidebar.adminTitle : t.sidebar.organizerTitle}</h2>
            <p>{t.sidebar.description}</p>
          </div>

          <nav className="admin-sidebar-nav">
            <div
              role="button"
              tabIndex={0}
              className={historyNavActive ? "active" : ""}
              onClick={onNavigateHistory}
              onKeyDown={(event) =>
                onSidebarItemKeyDown(event, onNavigateHistory)
              }
            >
              <span className="admin-sidebar-nav-icon" aria-hidden="true">
                <AdminSidebarIcon item="history" />
              </span>
              <span className="admin-sidebar-nav-label">{t.tabs.history}</span>
            </div>
            <div
              role="button"
              tabIndex={0}
              className={tournamentsNavActive ? "active" : ""}
              onClick={onNavigateTournaments}
              onKeyDown={(event) =>
                onSidebarItemKeyDown(event, onNavigateTournaments)
              }
            >
              <span className="admin-sidebar-nav-icon" aria-hidden="true">
                <AdminSidebarIcon item="tournaments" />
              </span>
              <span className="admin-sidebar-nav-label">
                {t.tabs.tournaments}
              </span>
            </div>
            {(isAdmin || isOrganizer) && (
              <a href="#/admin/ligas" className={leaguesNavActive ? "active" : undefined} aria-current={leaguesNavActive ? "page" : undefined} onClick={onClose}>
                <span className="admin-sidebar-nav-icon" aria-hidden="true">
                  <span className="material-symbols-outlined">leaderboard</span>
                </span>
                <span className="admin-sidebar-nav-label">{getLeagueTexts().title}</span>
              </a>
            )}
            <div
              role="button"
              tabIndex={0}
              className={usersNavActive ? "active" : ""}
              onClick={onNavigateUsers}
              onKeyDown={(event) => onSidebarItemKeyDown(event, onNavigateUsers)}
            >
              <span className="admin-sidebar-nav-icon" aria-hidden="true">
                <AdminSidebarIcon item="users" />
              </span>
              <span className="admin-sidebar-nav-label">{t.tabs.users}</span>
            </div>
            {isAdmin && (
              <div
                role="button"
                tabIndex={0}
                className={`admin-sidebar-extra-nav ${
                  clubsNavActive ? "active" : ""
                }`.trim()}
                onClick={onNavigateClubs}
                onKeyDown={(event) =>
                  onSidebarItemKeyDown(event, onNavigateClubs)
                }
              >
                <span className="admin-sidebar-nav-icon" aria-hidden="true">
                  <AdminSidebarIcon item="clubs" />
                </span>
                <span className="admin-sidebar-nav-label">
                  {t.tabs.clubs}
                </span>
              </div>
            )}
            {isAdmin && (
              <div
                role="button"
                tabIndex={0}
                className={`admin-sidebar-extra-nav ${
                  categoryRequestsNavActive ? "active" : ""
                }`.trim()}
                onClick={onNavigateCategoryRequests}
                onKeyDown={(event) =>
                  onSidebarItemKeyDown(event, onNavigateCategoryRequests)
                }
              >
                <span className="admin-sidebar-nav-icon" aria-hidden="true">
                  <AdminSidebarIcon item="categoryRequests" />
                </span>
                <span className="admin-sidebar-nav-label">
                  {t.categoryRequestsTab.title}
                </span>
              </div>
            )}
            {isAdmin && (
              <div
                role="button"
                tabIndex={0}
                className={`admin-sidebar-extra-nav ${
                  couponsNavActive ? "active" : ""
                }`.trim()}
                onClick={onNavigateCoupons}
                onKeyDown={(event) =>
                  onSidebarItemKeyDown(event, onNavigateCoupons)
                }
              >
                <span className="admin-sidebar-nav-icon" aria-hidden="true">
                  <AdminSidebarIcon item="coupons" />
                </span>
                <span className="admin-sidebar-nav-label">
                  {t.tabs.coupons}
                </span>
              </div>
            )}
            <div
              role="button"
              tabIndex={0}
              className={registrationsNavActive ? "active" : ""}
              onClick={onNavigateRegistrations}
              onKeyDown={(event) =>
                onSidebarItemKeyDown(event, onNavigateRegistrations)
              }
            >
              <span className="admin-sidebar-nav-icon" aria-hidden="true">
                <AdminSidebarIcon item="registrations" />
              </span>
              <span className="admin-sidebar-nav-label">
                {t.tabs.registrations}
              </span>
            </div>
            {(isAdmin || isOrganizer) && (
              <div
                role="button"
                tabIndex={0}
                className={paymentsNavActive ? "active" : ""}
                onClick={onNavigatePayments}
                onKeyDown={(event) =>
                  onSidebarItemKeyDown(event, onNavigatePayments)
                }
              >
                <span className="admin-sidebar-nav-icon" aria-hidden="true">
                  <AdminSidebarIcon item="payments" />
                </span>
                <span className="admin-sidebar-nav-label">
                  Split
                </span>
              </div>
            )}
          </nav>

          <div className="admin-sidebar-roadmap">
            <span className="admin-sidebar-roadmap-title">
              {t.sidebar.soon}
            </span>
            <span>
              <span className="admin-sidebar-nav-icon" aria-hidden="true">
                <AdminSidebarIcon item="stock" />
              </span>
              {t.sidebar.stockAndPayments}
            </span>
          </div>
        </div>
      </aside>
    </>
  );
}
